import {
  useMemo,
  type ComponentType,
  type MouseEvent as ReactMouseEvent,
  type SVGProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import { DOWNLOAD_FILE_NOT_OK_MESSAGE, downloadFile } from '@/utils';
import { isRnWebView } from '@/utils/function';
import { resolveDownloadUrl } from '@/utils/approval/resolveDownloadUrl';
import DownloadIcon from '@/public/icons/approval/download.svg';
import FileIcon from '@/public/icons/approval/attachment-file.svg';
import FileDocxIcon from '@/public/icons/approval/attachment-docx.svg';
import FileHwpIcon from '@/public/icons/approval/attachment-hwp.svg';
import FileImgIcon from '@/public/icons/approval/attachment-img.svg';
import FilePdfIcon from '@/public/icons/approval/attachment-pdf.svg';
import FilePptIcon from '@/public/icons/approval/attachment-ppt.svg';
import FileTxtIcon from '@/public/icons/approval/attachment-txt.svg';
import FileXlsxIcon from '@/public/icons/approval/attachment-xlsx.svg';
import FileZipIcon from '@/public/icons/approval/attachment-zip.svg';
import {
  AttachmentDownCell,
  AttachmentNameCell,
  AttachmentRowName,
  AttachmentRowSize,
  AttachmentThumb,
  AttachmentThumbCell,
  AttachmentsRoot,
  AttachmentsTable,
  AttachmentsTableBodyRow,
  AttachmentsTableHeadRow,
} from './styles';
import type { ApprovalViewAttachment } from './types';

type FileIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const FILE_ICON_BY_EXT: Record<string, FileIconComponent> = {
  pdf: FilePdfIcon,
  doc: FileDocxIcon,
  docx: FileDocxIcon,
  hwp: FileHwpIcon,
  hwpx: FileHwpIcon,
  ppt: FilePptIcon,
  pptx: FilePptIcon,
  xls: FileXlsxIcon,
  xlsx: FileXlsxIcon,
  csv: FileXlsxIcon,
  txt: FileTxtIcon,
  zip: FileZipIcon,
  rar: FileZipIcon,
  '7z': FileZipIcon,
};

const IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp']);

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

/**
 * 표시 파일명 — 원본명은 FILE_DESC(fileDescription)에 저장됨.
 * 비어 있는 과거/레거시 이관 문서는 fileName(스토리지 키) 폴백.
 */
const displayFileName = (a: ApprovalViewAttachment): string =>
  a.fileDescription?.trim() || a.fileName;

/** 파일 크기 표시 ("2.3 KB"). 0 / 음수는 "-" */
const formatSize = (size: number): string => {
  if (!size || size < 0) return '-';
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};

type AttachmentsReadOnlyProps = {
  attachments: ApprovalViewAttachment[];
};

/**
 * 첨부 목록 read-only — 표시 가능한 첨부(!isDeleted) 0건이면 null 반환해 영역 숨김.
 * IMG_EXTS/getExt 매핑은 이미지 모달/프리뷰 분기에 재사용 예정이라 보존.
 */
const AttachmentsReadOnly = ({ attachments }: AttachmentsReadOnlyProps) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();

  // await 뒤라 팝업 차단될 수 있어, 막히면 알림 버튼(사용자 클릭)으로 다시 연다
  const openInNewTab = (link: string) => {
    const opened = window.open(link, '_blank');
    if (opened) {
      opened.opener = null;
      return;
    }
    showError({
      message: t('파일을 다운로드할 수 없습니다.'),
      duration: null,
      action: {
        text: t('새 창에서 열기'),
        onClick: () => window.open(link, '_blank', 'noopener,noreferrer'),
      },
    });
  };

  type Row = {
    fileId: string;
    fileUuid: string | null;
    name: string;
    sizeText: string;
    url: string | undefined;
    isImage: boolean;
    ext: string;
  };

  /**
   * 응답의 `url` 은 짧게 만료되는 서명 URL 이라 문서를 열어 둔 채 시간이 지나면 실패한다.
   * 클릭 시점에 fileUuid 로 재발급받아 사용 (PORTAL-13977).
   */
  const handleDownload = async (
    e: ReactMouseEvent<HTMLAnchorElement>,
    row: Row,
  ) => {
    e.preventDefault();
    const link = await resolveDownloadUrl(row.fileUuid, row.url);
    if (!link) return;
    // 앱 웹뷰는 blob 다운로드가 안 돼 외부 브라우저로 넘긴다
    if (isRnWebView()) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ actionType: 'OPEN_BROWSER', url: link }),
      );
      return;
    }
    const result = await downloadFile({ link, name: row.name });
    if (result === true) return;
    // 파일이 없거나 권한 오류면 새 창도 같은 오류라 안내만
    if (result === DOWNLOAD_FILE_NOT_OK_MESSAGE) {
      showError({ message: t('파일을 다운로드할 수 없습니다.') });
      return;
    }
    // 직접 받기가 막히면(CORS 등) 새 창으로 열어 브라우저에 맡긴다
    openInNewTab(link);
  };

  const rows: Row[] = useMemo(
    () =>
      attachments
        .filter((a) => !a.isDeleted)
        .map((a) => {
          const name = displayFileName(a);
          const ext = (
            a.fileFormat ||
            getExt(name) ||
            getExt(a.fileName)
          ).toLowerCase();
          const isImage = IMG_EXTS.has(ext) || a.mimeType === 'image';
          return {
            fileId: a.fileId,
            fileUuid: a.fileUuid ?? null,
            name,
            sizeText: formatSize(a.fileSize),
            url: a.url || undefined,
            isImage,
            ext,
          };
        }),
    [attachments],
  );

  if (rows.length === 0) return null;

  return (
    <AttachmentsRoot>
      <AttachmentsTable>
        <colgroup>
          <col style={{ width: '48px' }} />
          <col />
          <col style={{ width: '120px' }} />
        </colgroup>
        <thead>
          <AttachmentsTableHeadRow>
            <AttachmentThumbCell />
            <AttachmentNameCell>{t('첨부파일')}</AttachmentNameCell>
            <AttachmentDownCell>{t('Down')}</AttachmentDownCell>
          </AttachmentsTableHeadRow>
        </thead>
        <tbody>
          {rows.map((r) => {
            const IconComp =
              FILE_ICON_BY_EXT[r.ext] || (r.isImage ? FileImgIcon : FileIcon);
            return (
              <AttachmentsTableBodyRow key={r.fileId}>
                <AttachmentThumbCell as="td">
                  <AttachmentThumb>
                    {r.isImage && r.url ? (
                      <img src={r.url} alt="" />
                    ) : (
                      <IconComp width={24} height={24} />
                    )}
                  </AttachmentThumb>
                </AttachmentThumbCell>
                <AttachmentNameCell as="td">
                  <AttachmentRowName
                    href={r.url ?? '#'}
                    onClick={(e) => handleDownload(e, r)}
                    aria-label={r.name}
                    title={t('Click to download')}
                  >
                    {r.name}
                  </AttachmentRowName>
                </AttachmentNameCell>
                <AttachmentDownCell as="td">
                  <AttachmentRowSize
                    href={r.url ?? '#'}
                    onClick={(e) => handleDownload(e, r)}
                    title={t('Click to download')}
                  >
                    <DownloadIcon width={12} height={12} />
                    {r.sizeText}
                  </AttachmentRowSize>
                </AttachmentDownCell>
              </AttachmentsTableBodyRow>
            );
          })}
        </tbody>
      </AttachmentsTable>
    </AttachmentsRoot>
  );
};

export default AttachmentsReadOnly;
